import { useId, useMemo, useState } from "react";
// import { Plus } from "lucide-react"; — used by the hidden "Log a contact" button below.
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client/react";
import { ChoiceChips, Heading, SearchInput, Skeleton, StatusBadge, Text } from "@athena/ui";
import { ClientsDocument, OutreachQueueDocument } from "@/graphql/clients.generated";
import {
    API_TAG_FOR_TAG,
    QUEUE_TAGS,
    queueDay,
    toQueueEntry,
    type QueueEntry,
    type QueueTag,
} from "./helpers/outreachQueue";
import { LogContactDialog, type LoggedContact } from "./components/LogContactDialog";
import type { ClientRecord } from "./helpers/client";
import { fullName, toClientRecord } from "./helpers/client";
import { useLogContact } from "@/hooks/useLogContact";
import { OutreachQueueItem } from "./components/OutreachQueueItem";
// import { PILL_BUTTON } from "./LogContactForm"; — used by the hidden "Log a contact" button below.

/**
 * A filter tag, and the heading its entries are drawn under. The API puts every entry under exactly
 * one (`OutreachQueueTag`): "All" draws them all, and its count is theirs added up.
 */
type QueueGroup = QueueTag;
type QueueFilter = "all" | QueueGroup;

const QUEUE_FILTERS = [
    "all",
    ...QUEUE_TAGS,
] as const satisfies readonly QueueFilter[];

/** Each group's count badge, colored by how urgent the section is. */
const GROUP_VARIANT = {
    overdue: "destructive",
    today: "warning",
    thisWeek: "sky",
    milestones: "sky",
    scheduled: "sky",
} as const satisfies Record<QueueGroup, "destructive" | "warning" | "sky">;

/** The API's largest page. The queue does not page yet; past this it says how many it is not showing. */
const QUEUE_PAGE_LIMIT = 200;

/**
 * Who the coordinator needs to reach: overdue (missed follow-ups included), due today and due this
 * week, then birthdays and sober anniversaries, then follow-ups still ahead. Each entry is under one
 * heading and one filter, the tag the API gave it, so the filters' counts add up to "All".
 *
 * The API decides all of it — who is due, when, and in which section — and searches and filters in
 * the database (`Query.outreachQueue`). A logged contact is saved and the queue re-read, so the
 * client moves to wherever their next due date puts them, usually out of the queue.
 */
export function OutreachQueuePage() {
    const { t } = useTranslation();
    const ids = useId();
    const today = useMemo(() => new Date(), []);
    const logContact = useLogContact();

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        filter,
        setFilter,
    ] = useState<QueueFilter>("all");
    const [
        expandedId,
        setExpandedId,
    ] = useState<string | undefined>();
    // Unset when "Log a contact" opens the dialog for anyone. Kept after the dialog closes so its
    // text does not change while it animates away.
    const [
        logEntry,
        setLogEntry,
    ] = useState<QueueEntry | undefined>();
    const [
        logOpen,
        setLogOpen,
    ] = useState<boolean>(false);

    const trimmedSearch = search.trim();
    const { data, previousData, loading, error } = useQuery(OutreachQueueDocument, {
        variables: {
            ...queueDay(today),
            search: trimmedSearch || undefined,
            tag: filter === "all" ? undefined : API_TAG_FOR_TAG[filter],
            limit: QUEUE_PAGE_LIMIT,
        },
    });
    // Keep showing the last answer while a new search or filter loads, rather than flashing empty.
    const queue = (data ?? previousData)?.outreachQueue;
    const entries = useMemo(() => (queue?.entries ?? []).map(toQueueEntry), [queue]);

    // Only for choosing who, when "Log a contact" opens without a queue entry.
    const { data: clientsData } = useQuery(ClientsDocument);
    const clients = useMemo<ClientRecord[]>(() => (clientsData?.clients ?? []).map(toClientRecord), [clientsData]);

    const groupCounts: Record<QueueGroup, number> = {
        overdue: queue?.counts.overdue ?? 0,
        today: queue?.counts.dueToday ?? 0,
        thisWeek: queue?.counts.thisWeek ?? 0,
        milestones: queue?.counts.milestones ?? 0,
        scheduled: queue?.counts.scheduled ?? 0,
    };
    const counts: Record<QueueFilter, number> = {
        ...groupCounts,
        all: QUEUE_TAGS.reduce((sum, group) => sum + groupCounts[group], 0),
    };
    const groups: readonly QueueGroup[] = filter === "all" ? QUEUE_TAGS : [filter];
    const sections = groups
        .map((group) => ({
            group,
            // Filtered here as well as in the API: the last answer stays on screen while the next loads.
            entries: entries.filter((entry) => entry.tag === group),
        }))
        .filter((group) => group.entries.length > 0);
    const narrowed = trimmedSearch !== "" || filter !== "all";

    const openLog = (entry: QueueEntry | undefined) => {
        setExpandedId(undefined);
        setLogEntry(entry);
        setLogOpen(true);
    };

    /** The dialog is about the entry it was opened from, or whoever was picked in it. */
    const logFromDialog = (contact: LoggedContact) => {
        const client = [
            ...(logEntry ? [logEntry.client] : []),
            ...clients,
        ].find((candidate) => candidate.id === contact.clientId);
        return logContact(contact, client ? fullName(client) : "");
    };

    return (
        <div className="flex flex-col gap-6 px-4 py-6 md:px-10 md:py-8">
            <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-2xl">
                    <Heading level={2} as="h1">
                        {t("admin.alumni.queue.title")}
                    </Heading>
                    <Text muted className="mt-2">
                        {t("admin.alumni.queue.description")}
                    </Text>
                </div>
                {/*
                    Hidden for now, both of them: the coordinator control until the coordinator toggle is
                    built, and "Log a contact" with it. Restore with their imports (Button, Eyebrow, Plus,
                    PILL_BUTTON) and the skipped header test in OutreachQueuePage.test.tsx.

                <div className="flex flex-wrap items-center gap-3">
                    <p className="flex items-center gap-3">
                        <Eyebrow as="span">{t("admin.alumni.queue.coordinatorLabel")}</Eyebrow>
                        <span className="border-border bg-card text-text-heading rounded-full border px-4 py-1.5 text-sm font-semibold">
                            {t("admin.alumni.queue.coordinatorMe")}
                        </span>
                    </p>
                    <Button
                        variant="secondary"
                        className={PILL_BUTTON}
                        leftIcon={<Plus className="h-4 w-4" />}
                        onClick={() => openLog(undefined)}
                    >
                        {t("admin.alumni.queue.logContact")}
                    </Button>
                </div>
                */}
            </header>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
                <div className="w-full shrink-0 lg:w-72">
                    <SearchInput
                        label={t("admin.alumni.queue.searchLabel")}
                        className="bg-card h-10 rounded-full"
                        value={search}
                        onValueChange={setSearch}
                    />
                </div>
                <ChoiceChips
                    aria-label={t("admin.alumni.queue.filterLabel")}
                    value={filter}
                    onValueChange={(value) => setFilter(value as QueueFilter)}
                    options={QUEUE_FILTERS.map((key) => ({
                        value: key,
                        label: t("admin.alumni.queue.filterOption", {
                            label: key === "all" ? t("admin.alumni.queue.all") : t(`admin.alumni.queue.section.${key}`),
                            count: counts[key],
                        }),
                    }))}
                />
            </div>

            {!queue && loading ? (
                <div className="grid gap-3" aria-busy="true">
                    {[
                        0,
                        1,
                        2,
                    ].map((row) => (
                        <Skeleton key={row} className="h-24 w-full rounded-2xl" />
                    ))}
                </div>
            ) : error && !queue ? (
                <Text size="sm" muted className="py-16 text-center">
                    {t("admin.alumni.queue.loadFailed")}
                </Text>
            ) : sections.length === 0 ? (
                <div className="py-16 text-center">
                    <Text weight="semibold" className="text-text-heading">
                        {narrowed ? t("admin.alumni.queue.noMatch") : t("admin.alumni.queue.empty")}
                    </Text>
                    {narrowed ? (
                        <Text size="sm" muted className="mt-1">
                            {t("admin.alumni.queue.noMatchHint")}
                        </Text>
                    ) : null}
                </div>
            ) : (
                <div className="grid gap-8">
                    {sections.map(({ group, entries: sectionEntries }) => (
                        <section key={group} aria-labelledby={`${ids}-${group}`}>
                            <div className="mb-3 flex items-center gap-2.5">
                                <Heading level={5} as="h2" id={`${ids}-${group}`}>
                                    {t(`admin.alumni.queue.section.${group}`)}
                                </Heading>
                                <StatusBadge variant={GROUP_VARIANT[group]} className="border-transparent font-mono">
                                    {counts[group]}
                                </StatusBadge>
                            </div>
                            <ul className="grid gap-3">
                                {sectionEntries.map((entry) => (
                                    <li key={entry.id}>
                                        <OutreachQueueItem
                                            entry={entry}
                                            today={today}
                                            expanded={expandedId === entry.id}
                                            onExpandedChange={(expanded) =>
                                                setExpandedId(expanded ? entry.id : undefined)
                                            }
                                            onLog={(contact) => logContact(contact, fullName(entry.client))}
                                            onOpenFullLog={() => openLog(entry)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                    {queue && queue.total > entries.length ? (
                        <Text size="sm" muted>
                            {t("admin.alumni.queue.moreNotShown", { shown: entries.length, total: queue.total })}
                        </Text>
                    ) : null}
                </div>
            )}

            <LogContactDialog
                open={logOpen}
                onOpenChange={setLogOpen}
                client={logEntry ? { id: logEntry.client.id, name: fullName(logEntry.client) } : undefined}
                clients={clients}
                defaultChannel={logEntry?.channel}
                today={today}
                onLog={logFromDialog}
            />
        </div>
    );
}
